import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarRail,
} from '@repo/ui/components/ui/sidebar'
import { Link } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import { Sparkles } from 'lucide-react'
import type * as React from 'react'
import { authClient } from '@/lib/auth-client'
import { NAV_ITEMS, type NavItemConfig } from '@/lib/config/navigation'
import { resolveIcon } from '@/lib/config/icon-map'
import { useOperatorConfiguration } from '@/lib/operator-config/use-operator-configuration'
import { deriveSubscriptionFromUser, featureRequiresPlan } from '@/lib/subscription'
import { AppLogo } from './logo'
import { NavMain } from './nav-main'
import { NavUser } from './nav-user'
import { SidebarUtilities } from './sidebar-utilities'

type NavEntry = {
  title: string
  url: string
  group: 'operations' | 'setup' | 'business' | 'admin'
  icon?: LucideIcon
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { data: session } = authClient.useSession()
  const { onboardingComplete, isLoading: isOnboardingLoading } = useOperatorConfiguration()

  const user = session?.user
    ? {
        name: session.user.name || 'User',
        email: session.user.email,
        avatar: session.user.image || '',
      }
    : {
        name: 'Guest',
        email: 'guest@example.com',
        avatar: '',
      }

  const subscription = deriveSubscriptionFromUser(session?.user)
  const userRole = (session?.user as { role?: string } | undefined)?.role
  const isAdmin = userRole === 'admin'

  const filterByAccess = (item: NavItemConfig) => {
    const planAllowed = featureRequiresPlan(subscription.planId, item.requiresPlan)
    const roleAllowed = !item.requiresRole || (item.requiresRole === 'admin' && isAdmin)
    return planAllowed && roleAllowed
  }

  const mapNavItem = (item: NavItemConfig): NavEntry => ({
    title: item.title,
    url: item.href,
    group: item.group ?? 'operations',
    icon: resolveIcon(item.icon),
  })

  const navItems = NAV_ITEMS.filter(filterByAccess).map(mapNavItem)
  const primaryAction = isOnboardingLoading || onboardingComplete
    ? null
    : {
        href: '/setup',
        label: 'Finish Setup',
        tooltip: 'Finish Setup',
        icon: Sparkles,
      }
  return (
    <Sidebar
      collapsible="icon"
      className="app-sidebar-shell border-r border-sidebar-border/70 backdrop-blur-sm"
      {...props}
    >
      <SidebarHeader>
        <AppLogo />
      </SidebarHeader>
      <SidebarContent>
        {primaryAction ? (
          <SidebarGroup>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  className="cursor-pointer rounded-[4px] bg-primary text-primary-foreground shadow-[var(--app-shadow-soft)] transition-all hover:bg-primary/90 hover:text-primary-foreground active:scale-[0.98] group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0"
                  tooltip={primaryAction.tooltip}
                >
                  <Link to={primaryAction.href}>
                    <primaryAction.icon className="size-4" />
                    <span className="font-medium group-data-[collapsible=icon]:hidden">
                      {primaryAction.label}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        ) : null}
        <NavMain items={navItems} />
      </SidebarContent>
      <SidebarFooter>
        <SidebarUtilities />
        <SidebarSeparator className="mx-0" />
        <NavUser user={user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
